import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Slider,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import StyleIcon from '@mui/icons-material/Style';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_STYLE_TRANSFER_STATE,
  buildStyleTransferRequest,
  hasSourceImage,
  type StyleTransferFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';
import { ReferenceImageInput } from '../references/ReferenceImageInput';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  onResult?: (result: OperationResult) => void;
}

export const StyleTransferPanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  onResult,
}) => {
  const [state, setState] = useState<StyleTransferFormState>(DEFAULT_STYLE_TRANSFER_STATE);
  const [styleRef, setStyleRef] = useState<SourceImageRef | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.styleTransfer);

  const hasSource = hasSourceImage(source);
  const hasStyleRef = !!styleRef;

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to apply a style.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildStyleTransferRequest(state, sid, source, styleRef);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled =
    mutation.isPending || isCreatingSession || !hasSource || !hasStyleRef;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <StyleIcon fontSize="small" />
        <Typography variant="subtitle2">Style transfer</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image — the content you want styled.</Alert>
      )}

      <ReferenceImageInput
        label="Style reference"
        helperText="The image whose visual style is transferred"
        value={styleRef}
        onChange={setStyleRef}
      />

      <Box>
        <Typography variant="caption" color="text.secondary">
          Style strength · {state.styleStrength.toFixed(2)}
        </Typography>
        <Slider
          value={state.styleStrength}
          onChange={(_, v) =>
            setState(s => ({ ...s, styleStrength: Array.isArray(v) ? v[0] : v }))
          }
          min={0}
          max={1}
          step={0.05}
          size="small"
        />
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={state.preserveContent}
            onChange={e => setState(s => ({ ...s, preserveContent: e.target.checked }))}
            size="small"
          />
        }
        label="Preserve content"
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Style transfer failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <StyleIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Stylizing…' : 'Apply style'}
        </Button>
      </Box>
    </Stack>
  );
};
